import { doc, setDoc, getDoc, deleteDoc, collection, query, getDocs, limit, arrayUnion, updateDoc } from 'firebase/firestore';
import { db as firestore } from '../firebaseConfig';
import { Player } from '../types';
import { generateId } from './helpers';

// Invite Token Structure
export interface InviteToken {
    id: string;
    type: 'group' | 'tournament';
    groupId: string;
    groupName: string;
    tournamentId?: string | null;
    tournamentName?: string | null;
    createdBy: string;
    createdByName: string;
    createdAt: string;
    expiresAt: string;
    maxUses: number; // -1 for unlimited
    usedCount: number;
    usedBy: string[]; // Track who used it
}

/**
 * Create a unique invite token for a group or tournament
 */
export async function createInviteToken(
    type: 'group' | 'tournament',
    groupId: string,
    groupName: string,
    createdBy: string,
    createdByName: string,
    tournamentId?: string,
    tournamentName?: string,
    expiryDays: number = 7,
    maxUses: number = -1 // -1 = unlimited
): Promise<InviteToken> {
    // const tokenId = generateId('inv'); // Moved down
    const now = new Date();

    // 1. For single-use tokens (maxUses = 1), ALWAYS create a new one (disposable links)
    // Only try to reuse for unlimited tokens
    let tokenId = '';
    
    if (maxUses === -1) {
        // Use deterministic IDs for permanent tokens
        tokenId = type === 'tournament' ? `inv_perm_t_${tournamentId}` : `inv_perm_g_${groupId}`;
        try {
            const tokenSnap = await getDoc(doc(firestore, 'inviteTokens', tokenId));
            if (tokenSnap.exists()) {
                const existingToken = tokenSnap.data() as InviteToken;
                // Double check it's unlimited and not expired just in case
                if ((existingToken.maxUses === -1 || existingToken.usedCount < existingToken.maxUses) && new Date(existingToken.expiresAt) > now) {
                    console.log("Reusing deterministic permanent invite token:", existingToken.id);
                    return existingToken;
                }
            }
        } catch (e) {
            console.warn("Error checking existing tokens deterministically:", e);
        }
    } else {
        // 2. Create new random token for disposable ones
        tokenId = generateId('inv');
    }
    const expiresAt = new Date(now.getTime() + expiryDays * 24 * 60 * 60 * 1000);

    const token: InviteToken = {
        id: tokenId,
        type,
        groupId,
        groupName,
        tournamentId: tournamentId || null,
        tournamentName: tournamentName || null,
        createdBy,
        createdByName,
        createdAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        maxUses,
        usedCount: 0,
        usedBy: []
    };

    await setDoc(doc(firestore, 'inviteTokens', tokenId), token);
    return token;
}

/**
 * Validate an invite token (Read-only)
 * Returns the token data if valid, null otherwise. Does NOT mark it as used.
 */
export async function validateInviteToken(tokenId: string): Promise<InviteToken | null> {
    try {
        const tokenRef = doc(firestore, 'inviteTokens', tokenId);
        const tokenSnap = await getDoc(tokenRef);

        if (!tokenSnap.exists()) {
            console.log('Token does not exist');
            return null;
        }

        const token = tokenSnap.data() as InviteToken;

        // Check expiry
        if (new Date(token.expiresAt) < new Date()) {
            console.log('Token expired');
            // We could clean up here, but for a read-only check, maybe better to just return null
            return null;
        }

        // Check max uses
        if (token.maxUses !== -1 && token.usedCount >= token.maxUses) {
            console.log('Token max uses reached');
            return null;
        }

        return token;
    } catch (error) {
        console.error('Error validating invite token:', error);
        return null;
    }
}

/**
 * Validate and use an invite token
 * Returns the token data if valid, null otherwise
 */
export async function validateAndUseInviteToken(
    tokenId: string,
    userId: string
): Promise<InviteToken | null> {
    try {
        const tokenRef = doc(firestore, 'inviteTokens', tokenId);
        const tokenSnap = await getDoc(tokenRef);

        if (!tokenSnap.exists()) {
            console.log('Token does not exist');
            return null;
        }

        const token = tokenSnap.data() as InviteToken;

        // Check expiry
        if (new Date(token.expiresAt) < new Date()) {
            console.log('Token expired');
            await deleteDoc(tokenRef);
            return null;
        }

        // Check max uses
        if (token.maxUses !== -1 && token.usedCount >= token.maxUses) {
            console.log('Token max uses reached');
            return null;
        }

        // Check if user already used this token (only for limited use tokens)
        if (token.maxUses !== -1 && token.usedBy.includes(userId)) {
            console.log('User already used this token - blocking reuse');
            // Block reuse: if user was kicked, they need a NEW invitation link
            return null;
        }

        // Update usage stats (only add user to list if not already there)
        const updatedUsedBy = token.usedBy.includes(userId) 
            ? token.usedBy 
            : [...token.usedBy, userId];

        await setDoc(tokenRef, {
            ...token,
            usedCount: token.usedBy.includes(userId) ? token.usedCount : token.usedCount + 1,
            usedBy: updatedUsedBy
        });

        return token;
    } catch (error) {
        console.error('Error validating invite token:', error);
        return null;
    }
}

/**
 * Generate the invite link URL
 */
export function generateInviteLink(tokenId: string): string {
    return `${window.location.origin}/?invite=${tokenId}`;
}

/**
 * Search users by pseudo (case-insensitive partial match)
 */
export async function searchUsersByPseudo(
    searchTerm: string,
    excludeIds: string[] = [],
    maxResults: number = 10
): Promise<Player[]> {
    if (!searchTerm || searchTerm.length < 2) return [];

    try {
        const lowerSearch = searchTerm.toLowerCase();

        // Firestore doesn't support LIKE queries, so we fetch and filter
        // For better performance with large user bases, consider Algolia or similar
        const usersRef = collection(firestore, 'users');
        const q = query(usersRef, limit(100)); // Limit initial fetch
        const snapshot = await getDocs(q);

        const results: Player[] = [];
        snapshot.forEach(doc => {
            const data = doc.data() as Player;
            const pseudo = (data.pseudo || data.name || '').toLowerCase();

            if (pseudo.includes(lowerSearch) && !excludeIds.includes(doc.id)) {
                results.push({ ...data, id: doc.id });
            }
        });

        return results.slice(0, maxResults);
    } catch (error) {
        console.error('Error searching users:', error);
        return [];
    }
}

/**
 * Send an invite notification to a user
 */
export async function sendInviteNotification(
    toUserId: string,
    fromUserId: string,
    fromUserName: string,
    type: 'group' | 'tournament',
    groupId: string,
    groupName: string,
    tournamentId?: string,
    tournamentName?: string
): Promise<boolean> {
    try {
        const notifId = generateId('notif');
        const now = new Date();

        const title = type === 'tournament'
            ? `Invitation au tournoi "${tournamentName}"`
            : `Invitation au groupe "${groupName}"`;

        const message = type === 'tournament'
            ? `${fromUserName} vous invite à rejoindre le tournoi "${tournamentName}" dans le groupe "${groupName}".`
            : `${fromUserName} vous invite à rejoindre le groupe "${groupName}".`;

        const notification = {
            id: notifId,
            type: 'tournament_invite' as const,
            playerId: toUserId,
            senderId: fromUserId,
            groupId,
            title,
            message,
            actionData: {
                type,
                groupId,
                groupName,
                tournamentId: tournamentId || null,
                tournamentName: tournamentName || null,
                invitedBy: fromUserId,
                invitedByName: fromUserName
            },
            createdAt: now.toISOString(),
            timestamp: now.toISOString(),
            read: false
        };

        await setDoc(doc(firestore, 'users', toUserId, 'notifications', notifId), notification);
        return true;
    } catch (error) {
        console.error('Error sending invite notification:', error);
        return false;
    }
}

/**
 * Accept an invite from notification (join group/tournament)
 */
export async function acceptInvite(
    userId: string,
    userData: Player,
    type: 'group' | 'tournament',
    groupId: string,
    tournamentId?: string | null
): Promise<{ success: boolean; message: string }> {
    try {
        if (type === 'group') {
            // Add user to group players sub-collection
            const playerRef = doc(firestore, 'groups', groupId, 'players', userId);
            const newPlayer = {
                id: userId,
                name: userData.displayName || userData.name || 'Joueur',
                pseudo: userData.pseudo || userData.displayName || 'Joueur',
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                avatar: userData.avatar || userData.photoURL || '',
                photoURL: userData.photoURL || '',
                hand: userData.hand || 'right',
                level: userData.level || '1. Niveau débutant',
                rating: userData.rating || 1500,
                rd: userData.rd || 350,
                volatility: userData.volatility || 0.06,
                joinedAt: new Date().toISOString(),
                groupJoinDate: new Date().toISOString(),
                badges: [],
                eloHistory: [],
                groupRole: 'player' as const
            };
            await setDoc(playerRef, newPlayer);

            // CRITICAL FIX: Add user to the group's 'members' array (for permissions and lists)
            const groupRef = doc(firestore, 'groups', groupId);
            await setDoc(groupRef, {
                members: arrayUnion(userId)
            }, { merge: true });

            return { success: true, message: 'Vous avez rejoint le groupe !' };
        } else if (type === 'tournament' && tournamentId) {
            // Check if user is already a member of the group
            const groupRef = doc(firestore, 'groups', groupId);
            const groupSnap = await getDoc(groupRef);

            if (groupSnap.exists()) {
                const groupData = groupSnap.data() as any;
                // If not a member, check privacy before auto-joining
                if (!groupData.members || !groupData.members.includes(userId)) {
                    // Privacy Check
                    const privacy = groupData.privacyMode || 'open';
                    if (privacy !== 'open') {
                        return {
                            success: false,
                            message: 'Ce groupe est privé. Vous devez être membre du groupe pour rejoindre ce tournoi.'
                        };
                    }

                    console.log("User not in group (Public), auto-joining before tournament entry...");
                    await acceptInvite(userId, userData, 'group', groupId);
                }
            }

            // Get tournament to check format
            const tournamentRef = doc(firestore, 'groups', groupId, 'tournaments', tournamentId);
            console.log(`[Invite Debug] Fetching tournament at: groups/${groupId}/tournaments/${tournamentId}`);

            const tournamentSnap = await getDoc(tournamentRef);
            console.log(`[Invite Debug] Tournament exists?`, tournamentSnap.exists());
            if (tournamentSnap.exists()) {
                console.log(`[Invite Debug] Tournament data:`, tournamentSnap.data());
            }

            if (!tournamentSnap.exists()) {
                return { success: false, message: 'Tournoi introuvable.' };
            }

            const tournament = tournamentSnap.data();
            const format = tournament.format?.toLowerCase() || '';

            // Always add to participants list first
            await updateDoc(tournamentRef, {
                participants: arrayUnion(userId)
            });

            if (format.includes('americano') || format.includes('mexicano')) {
                // Add to americano player IDs
                await updateDoc(tournamentRef, {
                    'config.americanoPlayerIds': arrayUnion(userId)
                });
                return { success: true, message: 'Vous avez rejoint le tournoi !' };
            } else {
                // Team format - user needs to create/join a team
                return { success: true, message: 'Inscription validée ! Créez votre équipe maintenant.' };
            }
        }

        return { success: false, message: 'Type d\'invitation invalide.' };
    } catch (error) {
        console.error('Error accepting invite:', error);
        return { success: false, message: 'Erreur lors de l\'acceptation.' };
    }
}
